// Quantity reconciliation: the drawn register against the model's own
// quantities, line by line, with the option to take the model's number.
//
// It only ever shows a comparison. Nothing changes in the view until someone
// ticks lines and presses Take model quantity, and every row that changes is
// marked for review with where the number came from.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, InputNumber, Table, Tag, Tooltip, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import MappingEditor from "./MappingEditor";
import { fmtFt, viewsApi } from "./viewModel";

const STATUS = {
  differs: { color: "orange", label: "Differs" },
  agrees: { color: "green", label: "Agrees" },
  only_2d: { color: "red", label: "Only in the drawings" },
  only_model: { color: "blue", label: "Only in the model" },
};

const delta = (v, unit) => {
  if (v == null || v === 0) return <span className="g-tsub">—</span>;
  return <span style={{ color: v > 0 ? "#237804" : "#a8071a" }}>{v > 0 ? "+" : ""}{fmtFt(v)}{unit}</span>;
};

const MEASURE_COLS = [["areaSf", "Area", "sf"], ["volumeCf", "Volume", "cf"], ["weightLb", "Weight", "lb"]];

export default function ReconcilePanel({ projectId, view, canEdit, onApplied }) {
  const [rec, setRec] = useState(view.reconciliation || null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState([]);
  const [tolerance, setTolerance] = useState((view.reconciliation && view.reconciliation.tolerancePct) ?? 5);
  const [blocked, setBlocked] = useState(null);
  const [mapping, setMapping] = useState(false);

  useEffect(() => { setRec(view.reconciliation || null); setPicked([]); }, [view.viewId, view.version, view.reconciliation]);

  const load = useCallback(async () => {
    try {
      const out = await viewsApi(projectId, `/${view.viewId}/reconcile`);
      if (out.reconciliation) setRec(out.reconciliation);
    } catch (e) { /* the panel still offers to run one */ }
  }, [projectId, view.viewId]);
  useEffect(() => { if (!view.reconciliation) load(); }, [load, view.reconciliation]);

  const run = async () => {
    setBusy(true); setBlocked(null);
    try {
      const out = await viewsApi(projectId, `/${view.viewId}/reconcile`, { method: "POST", body: { tolerancePct: tolerance } });
      setRec(out.reconciliation);
      setPicked([]);
      const s = out.reconciliation.summary || {};
      message.success(`${s.agrees} agree, ${s.differs} differ, ${s.only2d} only in the drawings, ${s.onlyModel} only in the model`);
    } catch (e) {
      setBlocked(e.message || "That did not run");
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const out = await viewsApi(projectId, `/${view.viewId}/reconcile/apply`, { method: "POST", body: { keys: picked } });
      setRec(out.reconciliation);
      setPicked([]);
      message.success(`${out.rows} row${out.rows === 1 ? "" : "s"} now carry the model's quantity, marked for review`);
      if (onApplied) onApplied();
    } catch (e) {
      message.error(e.message || "That did not apply");
    } finally { setBusy(false); }
  };

  const rows = useMemo(() => (rec && rec.rows ? rec.rows : []), [rec]);
  const takeable = useMemo(() => rows.filter((r) => r.model && r.rows && r.rows.length).map((r) => r.key), [rows]);

  const columns = [
    ...(canEdit ? [{
      title: <Checkbox checked={picked.length > 0 && picked.length === takeable.length} indeterminate={picked.length > 0 && picked.length < takeable.length}
                      onChange={(e) => setPicked(e.target.checked ? takeable : [])} />,
      dataIndex: "key", width: 40, render: (key, r) => (r.model && r.rows.length
        ? <Checkbox checked={picked.includes(key)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, key] : p.filter((k) => k !== key)))} />
        : null),
    }] : []),
    { title: "Type", dataIndex: "type", render: (t, r) => (
      <div>
        <div>{t}{r.size ? ` · ${r.size}` : ""}</div>
        <div className="g-tsub">{r.system}{r.levels && r.levels.length ? ` · ${r.levels.join(", ")}` : ""}{r.rows && r.rows.length ? ` · ${r.rows.length} row${r.rows.length === 1 ? "" : "s"}` : ""}</div>
      </div>
    ) },
    { title: "Model type", dataIndex: "model", render: (m, r) => (m
      ? <div><div>{m.type || m.family || "—"}</div><div className="g-tsub">{m.category}{r.confidence ? ` · match ${Math.round(r.confidence * 100)}%` : ""}</div></div>
      : <span className="g-tsub">no match</span>) },
    { title: "Qty drawings", dataIndex: "qty2d", align: "right", width: 90 },
    { title: "Qty model", dataIndex: "qtyModel", align: "right", width: 90, render: (v) => (v == null ? <span className="g-tsub">—</span> : v) },
    { title: "Δ qty", dataIndex: "deltaQty", align: "right", width: 80, render: (v) => delta(v, "") },
    { title: "Length drawings", dataIndex: "lengthFt2d", align: "right", width: 120, render: (v, r) => (v ? <Tooltip title={`${r.measuredRuns} run${r.measuredRuns === 1 ? "" : "s"} measured`}>{fmtFt(v)} ft</Tooltip> : <span className="g-tsub">not measured</span>) },
    { title: "Length model", dataIndex: "lengthFtModel", align: "right", width: 110, render: (v) => (v ? `${fmtFt(v)} ft` : <span className="g-tsub">—</span>) },
    { title: "Δ length", dataIndex: "deltaLengthFt", align: "right", width: 110, render: (v, r) => (
      <span>{delta(v, " ft")}{r.deltaLengthPct != null ? <span className="g-tsub"> ({r.deltaLengthPct}%)</span> : null}</span>) },
    // Area, volume and weight: from the model's own take-off; compared only
    // where the register states the measure too (the percentage).
    ...MEASURE_COLS.filter(([k]) => rows.some((r) => r[`${k}Model`] != null)).map(([k, label, unit]) => ({
      title: `${label} model`, dataIndex: `${k}Model`, align: "right", width: 110, render: (v, r) => (v == null ? <span className="g-tsub">—</span> : (
        <span>{fmtFt(v)} {unit}{r[`${k}2d`] != null ? <div className="g-tsub">drawings {fmtFt(r[`${k}2d`])}{r[`delta${k[0].toUpperCase()}${k.slice(1)}Pct`] != null ? ` (${r[`delta${k[0].toUpperCase()}${k.slice(1)}Pct`]}%)` : ""}</div> : null}</span>
      )),
    })),
    { title: "", dataIndex: "status", width: 150, render: (s, r) => (
      <span><Tag color={(STATUS[s] || {}).color}>{(STATUS[s] || {}).label || s}</Tag>{r.applied ? <Tag>taken</Tag> : null}</span>) },
  ];

  const s = (rec && rec.summary) || null;
  return (
    <section className="g-panel g-reconcile">
      <div className="g-viewhead">
        <div>
          <h2 className="g-h2">Quantities against the model</h2>
          <p className="g-sub">
            {rec
              ? <>Compared with model commit {String(rec.commitId).slice(-6)}{rec.exportVersion ? ` (export ${rec.exportVersion})` : ""} on {String(rec.at).slice(0, 10)}{rec.by ? ` by ${rec.by}` : ""}. Lengths in feet{rec.units === "metric" ? ", converted from the model's metres" : ""}.</>
              : <>Nothing compared yet. The model's own quantities come from the exported record files, read once per commit.</>}
          </p>
        </div>
        {canEdit && (
          <div className="g-actions g-noprint">
            <Tooltip title="How far apart two lengths may be and still count as agreeing">
              <InputNumber size="small" min={0} max={100} value={tolerance} onChange={(v) => setTolerance(v ?? 5)} addonAfter="%" style={{ width: 110 }} />
            </Tooltip>
            <Button size="small" icon={<ReloadOutlined />} loading={busy} onClick={run}>{rec ? "Compare again" : "Compare with the model"}</Button>
            <Tooltip title="Which of the model's categories each system reads: duct banks, cable trays, risers, for this view or the whole project">
              <Button size="small" onClick={() => setMapping(true)}>Edit system mapping</Button>
            </Tooltip>
            <Button size="small" type="primary" disabled={!picked.length} loading={busy} onClick={apply}>
              Take model quantity{picked.length ? ` (${picked.length})` : ""}
            </Button>
          </div>
        )}
      </div>

      {blocked && <div className="g-callout">{blocked}</div>}

      {s && (
        <div className="g-strip">
          <div><b>{s.agrees}</b><span>agree</span></div>
          <div><b>{s.differs}</b><span>differ</span></div>
          <div><b>{s.only2d}</b><span>only in the drawings</span></div>
          <div><b>{s.onlyModel}</b><span>only in the model</span></div>
          <div><b>{fmtFt(s.lengthFt2d)} ft</b><span>drawn length</span></div>
          <div><b>{fmtFt(s.lengthFtModel)} ft</b><span>model length</span></div>
          {MEASURE_COLS.filter(([k]) => s[`${k}Model`]).map(([k, label, unit]) => <div key={k}><b>{fmtFt(s[`${k}Model`])} {unit}</b><span>model {label.toLowerCase()}</span></div>)}
        </div>
      )}

      {rec && (
        <Table size="small" rowKey="key" columns={columns} dataSource={rows} pagination={rows.length > 50 ? { pageSize: 50 } : false}
               rowClassName={(r) => (r.status === "differs" ? "g-rowdiff" : "")} />
      )}
      <MappingEditor projectId={projectId} viewId={view.viewId} viewTitle={view.title} open={mapping}
                     onClose={() => setMapping(false)} onSaved={() => { if (rec) run(); }} />

      {rec && (
        <p className="g-tsub">
          A line is paired only when the model category is mapped to that system and the type names agree on size or share words.
          Unpaired lines are shown as they are rather than forced together. Edit the mapping from the button above the schedule if a
          system reads the wrong categories.
        </p>
      )}
    </section>
  );
}
